import { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Grid,
  Typography,
  Modal,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Autocomplete,
  Button,
  Stack,
  IconButton
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CloseIcon from '@mui/icons-material/Close';
import * as Yup from 'yup';
import { useFormik, Form, FormikProvider } from 'formik';
import jsPDF from 'jspdf';
import {
  applyPasswordAutofillWorkaround,
  extractHierarchyInfoFromPath,
  parseValidationErrorMessage,
  parseValidationErrorMessageForBulkUpload,
  computeHierarchyMeta,
  getEntityTypeAndId,
  isPasswordStrong,
  normalizeFailedUsers,
  passwordYupField,
  phoneYupField
} from '../../../utils/Utilities';
import { useAuth } from '../../customContextProviders/AuthContext';
import { AddNewUserToNox, BulkCreateUsers, UpdateUserToNox } from '../../../network/User';
import { AlertDialog, SnackBarAlert, PasswordRulesChecklist } from '../../HelperComponents';
import { ConfirmDialog } from '../app';
import {
  ADMIN_ONLY_ROLE_ORGS,
  APP_CONSTANTS,
  BRANCH_REQUIRED_ROLES,
  BULK_KEYS,
  ROLE_CONSTANTS,
  ROLE_OPTIONS,
  USER_BULK_SAMPLE_PATH
} from '../../../URLConstants';

// ─── Constants ────────────────────────────────────────────────────────────────

const MODAL_STYLE = {
  width: '100%',
  maxWidth: { sm: '90%', xs: '90%', md: 600 },
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: 3,
  borderRadius: 1
};

const BULK_USER_EXCEL_MIME_TYPES = new Set([
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-excel'
]);

function isValidBulkUserExcelFile(file) {
  const fileName = file.name?.toLowerCase() || '';
  return (
    fileName.endsWith('.xlsx') ||
    fileName.endsWith('.xls') ||
    BULK_USER_EXCEL_MIME_TYPES.has(file.type)
  );
}

function extractBulkUsersResult(response) {
  const body = response?.data;
  if (!body || typeof body !== 'object' || Array.isArray(body)) return null;
  const isBulkShape = (obj) =>
    obj &&
    typeof obj === 'object' &&
    !Array.isArray(obj) &&
    BULK_KEYS.some((key) => typeof obj[key] === 'number');

  if (isBulkShape(body)) return body;

  if (body.data && isBulkShape(body.data)) return body.data;
  return null;
}

function formatBulkUsersUploadMessage(payload) {
  const successCount = payload.successCount  ?? 0;
  const failureCount = payload.failureCount  ?? 0;
  const summary = `${successCount} success, ${failureCount} failed`;
  const failedUsers = normalizeFailedUsers(payload);

  const lines = failedUsers.map(({ email, errors }) => {
    const errStr = errors.join(', ');
    const label = email ? `${email}: ${errStr}` : errStr;
    return label.trim();
  }).filter(Boolean);

  if (lines.length === 0) return summary;
  return `${summary}\n${lines.join('\n')}`;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const getHierarchyFromPath = (path) => extractHierarchyInfoFromPath(path) ?? {};

const buildPayload = (values) =>
  Object.fromEntries(
    Object.entries({
      email: values.userEmail,
      password: values.userPassword,
      organizationId: values.organizationId,
      name: values.userName,
      phone: values.userPhone,
      role: values.userRole,
      // branchId: values.branchId,
      // headOffice: values.headOfficeName,
      // zoneOffice: values.zoneOfficeName,
      // regionalOffice: values.regionalOfficeName,
      username: values.employeeId
    }).filter(([, v]) => v !== '')
  );


function HierarchyField({
  isLocked,
  lockedValue,
  label,
  options,
  currentValue,
  onChange,
  disabled,
  error,
  helperText
}) {
  const ariaLabel = label.replaceAll('Office', 'office');
  if (isLocked) {
    return (
      <TextField
        fullWidth
        label={label}
        value={lockedValue}
        disabled
        inputProps={{ 'aria-label': ariaLabel }}
        error={Boolean(error)}
        helperText={helperText}
      />
    );
  }
  return (
    <Autocomplete
      options={options || []}
      getOptionLabel={(o) => o.name || ''}
      value={options?.find((o) => o.id === currentValue) || null}
      onChange={onChange}
      renderInput={(params) => (
        <TextField
          {...params}
          label={`Select ${label}`}
          inputProps={{
            ...params.inputProps,
            'aria-label': `Select ${ariaLabel}`
          }}
          error={Boolean(error)}
          helperText={helperText}
        />
      )}
      disabled={disabled}
    />
  );
}

HierarchyField.propTypes = {
  isLocked: PropTypes.bool,
  lockedValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  label: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      name: PropTypes.string
    })
  ),
  currentValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func,
  disabled: PropTypes.bool,
  error: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  helperText: PropTypes.oneOfType([PropTypes.string, PropTypes.bool])
};

// ─── Main Component ─────────────────────────────────────────────────────────

export default function AddUserToNoxModal({
  opneAssignedModal,
  handleClose,
  headOfficesList,
  zonesList,
  regionsList,
  branchesList,
  fetchZones,
  fetchRegions,
  fetchBranchesList,
  selectedUser,
  onAction
}) {
  const { sessionUserData, accountDetails, userLoginOrg } = useAuth();

  const isIobOrg = userLoginOrg === APP_CONSTANTS.IOB_CLIENT_IDENTIFIER;
  const isAdminOnlyRoleOrg = ADMIN_ONLY_ROLE_ORGS.has(userLoginOrg);

  const availableRoleOptions = useMemo(() => {
    if (selectedUser || !ADMIN_ONLY_ROLE_ORGS.has(userLoginOrg)) {
      return ROLE_OPTIONS;
    }
    return ROLE_OPTIONS.filter(({ value }) => value !== ROLE_CONSTANTS?.CHECKER && value !== ROLE_CONSTANTS?.MAKER);
  }, [selectedUser, userLoginOrg]);

  const validationSchema = useMemo(() => {
    const branchHierarchyRequiredForRole = (role) =>
      BRANCH_REQUIRED_ROLES.includes(role) && !isIobOrg && !isAdminOnlyRoleOrg;

    return Yup.object().shape({
      userName: Yup.string().required('Name is required'),
      userPassword: Yup.string().when('selectedUser', {
        is: false,
        then: () => passwordYupField()
      }),
      userPhone: phoneYupField(),
      userEmail: Yup.string().required('Email is required'),
      userRole: Yup.string().required('Role is required'),
      headOfficeName: Yup.string().required('Head Office is required'),
      zoneOfficeName: Yup.string().when('userRole', {
        is: (role) => branchHierarchyRequiredForRole(role),
        then: Yup.string().required('Zone Office is required')
      }),
      regionalOfficeName: Yup.string().when('userRole', {
        is: (role) => branchHierarchyRequiredForRole(role),
        then: Yup.string().required('Regional Office is required')
      }),
      branchId: Yup.string().when('userRole', {
        is: (role) => branchHierarchyRequiredForRole(role),
        then: Yup.string().required('Branch Office is required')
      })
    });
  }, [isIobOrg, isAdminOnlyRoleOrg]);

  // Lock flags derived from LOGGED-IN admin's hierarchy — not the selected user's
  const {
    hierarchyInfo: adminHierarchyInfo,
    isHeadOfficeLocked,
    isZoneOfficeLocked,
    isRegionalOfficeLocked
  } = useMemo(
    () => computeHierarchyMeta(accountDetails?.path, sessionUserData?.user?.role),
    [accountDetails?.path, sessionUserData?.user?.role]
  );

  const [snack, setSnack] = useState({
    open: false,
    title: '',
    severity: 'info',
    wrapContent: false,
    autoHideDuration: null
  });
  const [bulkUploadLoading, setBulkUploadLoading] = useState(false);
  const [bulkUploadConfirmOpen, setBulkUploadConfirmOpen] = useState(false);
  const [pendingBulkFile, setPendingBulkFile] = useState(null);
  const [bulkUploadAlertOpen, setBulkUploadAlertOpen] = useState(false);
  const [bulkUploadAlertMetas, setBulkUploadAlertMetas] = useState({
    message: '',
    title: '',
    bulkUploadError: null
  });
  const bulkFileInputRef = useRef(null);

  const showSnack = (title, severity = 'info') =>
    setSnack({
      open: true,
      title,
      severity,
      wrapContent: false,
      autoHideDuration: null
    });

  const showBulkUploadErrorAlert = (message) => {
    setBulkUploadAlertMetas({
      title: 'Error processing the file',
      message,
      bulkUploadError: message
    });
    setBulkUploadAlertOpen(true);
  };

  const exportBulkUploadErrorReport = () => {
    const doc = new jsPDF();
    const now = new Date();
    const formattedDate = now.toLocaleDateString();
    const formattedTime = now.toLocaleTimeString();
    const errorMessage =
      typeof bulkUploadAlertMetas.bulkUploadError === 'string'
        ? bulkUploadAlertMetas.bulkUploadError
        : JSON.stringify(bulkUploadAlertMetas.bulkUploadError, null, 2);

    doc.setFont('Helvetica', 'bold');
    doc.text(10, 11, 'Error Report');
    doc.setFont('Mulish', 'normal');
    doc.text(10, 20, `${formattedDate} ${formattedTime}`);
    doc.text(errorMessage || 'NA', 10, 30, { maxWidth: 190 });
    doc.save(`user_bulk_upload_error_${formattedDate}_${formattedTime}.pdf`);
    setBulkUploadAlertOpen(false);
  };

  const downloadUsersBulkSample = () => {
    const link = document.createElement('a');
    link.href = USER_BULK_SAMPLE_PATH;
    link.download = 'user_bulk_upload_sample.xlsx';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const resetBulkFileInput = () => {
    if (bulkFileInputRef.current) {
      bulkFileInputRef.current.value = null;
    }
  };

  const clearBulkUploadConfirmState = () => {
    if (bulkUploadLoading) return;
    setBulkUploadConfirmOpen(false);
    setPendingBulkFile(null);
    resetBulkFileInput();
  };

  const uploadBulkUsersFile = async (file) => {
    setBulkUploadLoading(true);
    try {
      const response = await BulkCreateUsers(file);
      const payload = extractBulkUsersResult(response);
      const failureCount = payload?.failureCount ?? 0;

      if (payload && failureCount === 0) {
        const successCount = payload.successCount ?? 0;
        const successMessage = successCount
          ? `${successCount} user(s) created successfully`
          : 'Users bulk upload completed successfully';
        showSnack(successMessage, 'success');
        handleClose();
        onAction?.();
        return;
      }

      if (payload && failureCount > 0) {
        showBulkUploadErrorAlert(formatBulkUsersUploadMessage(payload));
        return;
      }

      showSnack('Users bulk upload completed successfully', 'success');
      handleClose();
      onAction?.();
    } catch (error) {
      const errPayload = extractBulkUsersResult(error?.response);
      if (errPayload && (errPayload.failureCount ?? 0) > 0) {
        showBulkUploadErrorAlert(formatBulkUsersUploadMessage(errPayload));
      } else {
        showBulkUploadErrorAlert(parseValidationErrorMessageForBulkUpload(error));
      }
    } finally {
      setBulkUploadLoading(false);
      resetBulkFileInput();
    }
  };

  const handleBulkUsersFileChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!isValidBulkUserExcelFile(file)) {
      showSnack('Please upload only Excel files (.xlsx or .xls)', 'error');
      resetBulkFileInput();
      return;
    }

    resetBulkFileInput();
    setPendingBulkFile(file);
    setBulkUploadConfirmOpen(true);
  };

  const handleBulkUploadConfirm = async () => {
    if (!pendingBulkFile || bulkUploadLoading) return;

    try {
      await uploadBulkUsersFile(pendingBulkFile);
    } finally {
      clearBulkUploadConfirmState();
    }
  };

  // ─── Selected user's hierarchy (for edit mode prefill) ────────────────────

  const selectedUserHierarchy = useMemo(
    () => getHierarchyFromPath(selectedUser?.taxonomy?.path),
    [selectedUser]
  );

  const selectedUserRegionalOfficeId = useMemo(() => {
    if (!selectedUser) return '';
    return regionsList.find((r) => r.name === selectedUserHierarchy.regionalOffice)?.id || '';
  }, [selectedUser, regionsList]);

  const selectedUserZoneOfficeId = useMemo(() => {
    if (!selectedUser) return '';
    return zonesList.find((r) => r.name === selectedUserHierarchy.zoneOffice)?.id || '';
  }, [selectedUser, zonesList]);


  const initialValues = useMemo(
    () => ({
      userName: selectedUser?.name || '',
      userEmail: selectedUser?.email || '',
      userPassword: '',
      userPhone: selectedUser?.phone || '',
      userRole: selectedUser?.role || '',
      organizationId: sessionUserData?.user?.organizationId,
      employeeId: selectedUser?.username || '',
      headOfficeName: selectedUser ? selectedUserHierarchy.headOffice || '' : '',
      zoneOfficeName: selectedUser ? selectedUserHierarchy.zoneOffice || '' : '',
      regionalOfficeName: selectedUser ? selectedUserHierarchy.regionalOffice || '' : '',
      branchId: selectedUser ? selectedUserHierarchy.branchId || '' : '',
      selectedUser: !!selectedUser,
      regionalOfficeId: selectedUser ? selectedUserRegionalOfficeId || '' : '',
      zoneOfficeId: selectedUser ? selectedUserZoneOfficeId || '' : '',
    }),
    [selectedUser, sessionUserData, selectedUserHierarchy]
  );



  // ─── Formik ───────────────────────────────────────────────────────────────

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema,
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      try {
        const payload = {
          ...buildPayload(values),
          ...getEntityTypeAndId(values, headOfficesList, zonesList, regionsList, branchesList)
        };

        // Don't re-send username if unchanged in edit mode
        if (selectedUser && values.employeeId === selectedUser.username) {
          delete payload.username;
        }

        if (selectedUser) {
          await UpdateUserToNox(selectedUser.id, payload);
        } else {
          await AddNewUserToNox(payload);
        }

        showSnack(selectedUser ? 'Successfully Updated' : 'User Created Successfully', 'success');
        handleClose();
        resetForm();
        onAction?.();
      } catch (error) {
        showSnack(parseValidationErrorMessage(error), 'error');
      } finally {
        setSubmitting(false);
      }
    }
  });

  const { values, setFieldValue, errors, touched, getFieldProps } = formik;
  const userPasswordField = getFieldProps('userPassword');
  const passwordMeetsRules = isPasswordStrong(values.userPassword);
  const showPasswordErrorState = Boolean(touched.userPassword && errors.userPassword);

  useEffect(() => {
    if (!opneAssignedModal || selectedUser) return;

    const prefillForAdd = async () => {
      if (isHeadOfficeLocked && adminHierarchyInfo?.headOffice) {
        setFieldValue('headOfficeName', adminHierarchyInfo.headOffice);

        //  HO is locked as TextField but  is still an Autocomplete — fetch its options
        if (!isZoneOfficeLocked) {
          const headOffice = headOfficesList?.find((o) => o.name === adminHierarchyInfo.headOffice);
          fetchZones(headOffice.id);
        }
      }

      if (isZoneOfficeLocked && adminHierarchyInfo?.zoneOffice) {
        setFieldValue('zoneOfficeName', adminHierarchyInfo.zoneOffice);

        //  Zone is locked as TextField but Region is still an Autocomplete — fetch its options
        const freshZones =
          (await fetchZones(
            headOfficesList?.find((o) => o.name === adminHierarchyInfo.headOffice)?.id
          )) || [];
        const zone = freshZones.find((z) => z.name === adminHierarchyInfo.zoneOffice);
        fetchRegions(null, zone?.id);

      }

      if (isRegionalOfficeLocked && adminHierarchyInfo?.regionalOffice) {
        setFieldValue('regionalOfficeName', adminHierarchyInfo.regionalOffice);
        // Branch is always an Autocomplete — fetch its options
        const regionalOfficeId = regionsList.find((o) => o.name === adminHierarchyInfo.regionalOffice)?.id;
        fetchBranchesList(regionalOfficeId);
      }
    };

    prefillForAdd();
  }, [opneAssignedModal, selectedUser, headOfficesList]);

  useEffect(() => {
    if (!opneAssignedModal || !selectedUser) return;
    if (!headOfficesList?.length) return;

    const loadEditHierarchy = async () => {
      if (!selectedUserHierarchy?.headOffice) return;

      const headOffice = headOfficesList?.find((o) => o.name === selectedUserHierarchy.headOffice);
      if (!headOffice?.id) return;

      //  Always fetch zones — need zone.id to fetch regions
      const freshZones = (await fetchZones(headOffice.id)) || [];

      if (selectedUserHierarchy?.zoneOffice && !isRegionalOfficeLocked) {
        const zone = freshZones.find((z) => z.name === selectedUserHierarchy.zoneOffice);
        const freshRegions = await fetchRegions(null, zone.id);
        setFieldValue('regionalOfficeId', freshRegions.find((r) => r.name === selectedUserHierarchy.regionalOffice)?.id);
      }

      if (selectedUserHierarchy?.regionalOffice) {
        const regionalOfficeId = regionsList.find((r) => r.name === selectedUserHierarchy.regionalOffice)?.id;
        await fetchBranchesList(
          regionalOfficeId
        );
      }
    };

    loadEditHierarchy();
  }, [opneAssignedModal, selectedUser, headOfficesList]);

  // ─── Dropdown change handlers ─────────────────────────────────────────────

  const handleHeadOfficeChange = (_, val) => {
    setFieldValue('headOfficeName', val?.name || '');
    setFieldValue('zoneOfficeName', '');
    setFieldValue('regionalOfficeName', '');
    setFieldValue('branchId', '');
    if (val?.id) fetchZones(val.id);
  };

  const handleZoneChange = (_, val) => {
    setFieldValue('zoneOfficeName', val?.name || '');
    setFieldValue('zoneOfficeId', val?.id || '');
    setFieldValue('regionalOfficeName', '');
    setFieldValue('branchId', '');
    if (val?.id) fetchRegions(null, val.id);
  };

  const handleRegionChange = (_, val) => {
    setFieldValue('regionalOfficeName', val?.name || '');
    setFieldValue('regionalOfficeId', val?.id || '');
    setFieldValue('branchId', '');
    if (val?.name) {
      fetchBranchesList(val.id);
    }
  };

  const handleModalClose = () => {
    handleClose();
    formik.resetForm();
  };

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <Box>
      <Modal open={opneAssignedModal} onClose={handleModalClose}>
        <FormikProvider value={formik}>
          <Form autoComplete="off" noValidate onSubmit={formik.handleSubmit}>
            <Box sx={MODAL_STYLE} data-testid={selectedUser ? 'update_user' : 'create_user'}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                <Typography sx={{ fontWeight: 700, fontSize: 18 }}>
                  {selectedUser ? 'Update User' : 'Create User'}
                </Typography>
                <IconButton onClick={handleModalClose} aria-label="Close">
                  <CloseIcon />
                </IconButton>
              </Stack>

              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    label="Name"
                    autoComplete="off"
                    slotProps={{
                      htmlInput: {
                        autoComplete: 'off',
                        'data-lpignore': 'true',
                        'data-1p-ignore': 'true',
                        'data-form-type': 'other'
                      }
                    }}
                    {...formik.getFieldProps('userName')}
                  />
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    label="Employee ID"
                    {...formik.getFieldProps('employeeId')}
                  />
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    label="Mobile No"
                    inputMode="numeric"
                    slotProps={{ htmlInput: { maxLength: 10 } }}
                    error={touched.userPhone && Boolean(errors.userPhone)}
                    helperText={touched.userPhone && errors.userPhone}
                    {...formik.getFieldProps('userPhone')}
                  />
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    label="Email"
                    {...formik.getFieldProps('userEmail')}
                    error={Boolean(touched.userEmail && errors.userEmail)}
                    helperText={touched.userEmail && errors.userEmail}
                  />
                </Grid>

                {!selectedUser && (
                  <Grid size={{ xs: 12, md: 6 }}>
                    <PasswordRulesChecklist
                      password={values.userPassword}
                      showErrorState={showPasswordErrorState}
                      error={touched.userPassword && Boolean(errors.userPassword)}
                      helperText={touched.userPassword && errors.userPassword}
                    >
                      <TextField
                        {...applyPasswordAutofillWorkaround(userPasswordField)}
                        data-testid="user-password-input"
                        fullWidth
                        label="Password"
                      />
                    </PasswordRulesChecklist>
                  </Grid>
                )}

                {/* Role */}
                <Grid size={{ xs: 12, md: 6 }}>
                  <FormControl fullWidth error={Boolean(touched.userRole && errors.userRole)}>
                    <InputLabel id="user-role-select-label">Role</InputLabel>
                    <Select
                      labelId="user-role-select-label"
                      id="user-role-select"
                      value={values.userRole}
                      label="Role"
                      onChange={(e) => {
                        setFieldValue('userRole', e.target.value);
                        if (e.target.value === 'admin') setFieldValue('branchId', '');
                      }}
                    >
                      {availableRoleOptions.map(({ label, value }) => (
                        <MenuItem key={value} value={value}>
                          {label}
                        </MenuItem>
                      ))}
                    </Select>
                    {touched.userRole && errors.userRole ? (
                      <FormHelperText>{errors.userRole}</FormHelperText>
                    ) : null}
                  </FormControl>
                </Grid>

                {/* Head Office — locked TextField for HOAdmin/ZOAdmin/ROAdmin, Autocomplete otherwise */}
                <Grid size={{ xs: 12, md: 6 }}>
                  <HierarchyField
                    isLocked={isHeadOfficeLocked}
                    lockedValue={values.headOfficeName}
                    label="Head Office"
                    options={headOfficesList}
                    currentValue={values.headOfficeName}
                    onChange={handleHeadOfficeChange}
                    error={touched.headOfficeName && errors.headOfficeName}
                    helperText={touched.headOfficeName && errors.headOfficeName}
                  />
                </Grid>

                {/* Zone Office — locked TextField for ZOAdmin/ROAdmin, Autocomplete otherwise */}
                <Grid size={{ xs: 12, md: 6 }}>
                  <HierarchyField
                    isLocked={isZoneOfficeLocked}
                    lockedValue={isZoneOfficeLocked ? values.zoneOfficeName : values.zoneOfficeId}
                    label="Zone Office"
                    options={zonesList}
                    currentValue={values.zoneOfficeId}
                    onChange={handleZoneChange}
                    disabled={!values.headOfficeName}
                    error={touched.zoneOfficeName && errors.zoneOfficeName}
                    helperText={touched.zoneOfficeName && errors.zoneOfficeName}
                  />
                </Grid>

                {/* Regional Office — locked TextField for ROAdmin, Autocomplete otherwise */}
                <Grid size={{ xs: 12, md: 6 }}>
                  <HierarchyField
                    isLocked={isRegionalOfficeLocked}
                    lockedValue={isRegionalOfficeLocked ? values.regionalOfficeName : values.regionalOfficeId}
                    label="Regional Office"
                    options={regionsList}
                    currentValue={values.regionalOfficeId}
                    onChange={handleRegionChange}
                    disabled={!values.zoneOfficeName}
                    error={touched.regionalOfficeName && errors.regionalOfficeName}
                    helperText={touched.regionalOfficeName && errors.regionalOfficeName}
                  />
                </Grid>

                {/* Branch — hidden for banks without Checker/Maker roles */}
                {!isAdminOnlyRoleOrg && (
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Autocomplete
                      options={branchesList || []}
                      getOptionLabel={(o) => o.branchId || ''}
                      value={branchesList?.find((b) => b.branchId === values.branchId) || null}
                      onChange={(_, val) => setFieldValue('branchId', val?.branchId || '')}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Select Branch Office"
                          error={Boolean(touched.branchId && errors.branchId)}
                          helperText={touched.branchId && errors.branchId}
                        />
                      )}
                      disabled={
                        !values.userRole || values.userRole === ROLE_CONSTANTS.ADMIN || !values.regionalOfficeName
                      }
                    />
                  </Grid>
                )}
              </Grid>

              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: 2,
                  mt: 2
                }}
              >
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                  {!selectedUser && (
                    <>
                      <input
                        ref={bulkFileInputRef}
                        type="file"
                        accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                        hidden
                        onChange={handleBulkUsersFileChange}
                      />
                      <Button
                        variant="contained"
                        startIcon={<CloudUploadIcon />}
                        onClick={() => bulkFileInputRef.current?.click()}
                      >
                        Bulk create
                      </Button>
                      <Button variant="text" size="small" onClick={downloadUsersBulkSample}  sx={{
                          fontSize: 14,
                          fontWeight: 800,
                          color: 'blue',
                          textDecoration: 'underline',
                          cursor: 'pointer'
                        }}>
                        Download sample file
                      </Button>
                    </>
                  )}
                </Stack>
                <LoadingButton
                  loading={formik.isSubmitting}
                  type="submit"
                  variant="contained"
                  disabled={!selectedUser && !passwordMeetsRules}
                >
                  {selectedUser ? 'Update' : 'Create'}
                </LoadingButton>
              </Box>
            </Box>
          </Form>
        </FormikProvider>
      </Modal>

      <ConfirmDialog
        open={bulkUploadConfirmOpen}
        title="Bulk upload users"
        message={
          pendingBulkFile?.name
            ? `Are you Sure you want to upload ${pendingBulkFile.name} for Bulk user creation?`
            : 'Are you Sure you want to upload this file for Bulk user creation?'
        }
        confirmText="Upload"
        cancelText="Cancel"
        loading={bulkUploadLoading}
        onCancel={clearBulkUploadConfirmState}
        onConfirm={handleBulkUploadConfirm}
      />

      <AlertDialog
        openAlertDialog={bulkUploadAlertOpen}
        message={bulkUploadAlertMetas.message}
        title={bulkUploadAlertMetas.title}
        handleClose={() => setBulkUploadAlertOpen(false)}
        bulkUploadError={bulkUploadAlertMetas.bulkUploadError}
        exportErrorReport={exportBulkUploadErrorReport}
      />

      <SnackBarAlert
        open={snack.open}
        title={snack.title}
        severity={snack.severity}
        wrapContent={snack.wrapContent}
        autoHideDuration={snack.autoHideDuration}
        handleClose={() => setSnack((s) => ({ ...s, open: false }))}
      />
    </Box>
  );
}
